import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { useHistory } from 'react-router-dom'
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded'
import { useDispatch, useSelector } from 'react-redux'
import AppRoutes from '../../../services/AppRoutes'
import CookiesStorageService from '../../../services/CookiesStorageService'
import UseTranslation from '../../../hooks/UseTranslation'
import { ReactComponent as LoginBackSvg } from '../../../images/loginBackSvg.svg'
import { ReactComponent as ShieldCheckIcon } from '../../../images/ShieldCheck.svg'
import { selectAuthUser } from '../../../redux/features/userAuth/userAuthSlice'
import { decodeHtmlString } from '../../../utils/helpers'
import { userAuthAcceptPolicies, userAuthGetPolicies, userAuthResetAlertHandler } from '../../../redux/features/userAuth/userAuthThunk'
import LoaderWithErrorHandler from '../../../components/PageHelpers/LoaderWithErrorHandler'
import SvgIconSize from '../../../components/Cards/helpers/SvgIconSize'
import { ReactComponent as LockIcon } from '../../../images/lock.svg'
import { ReactComponent as CheckSignIcon } from '../../../images/CheckSign.svg'
import DeclineConsentFormModal from './DeclineConsentFormModal'

const Consent = () => {
  const dispatch = useDispatch()
  const { t, language } = UseTranslation()
  const history = useHistory()
  const contentRef = useRef(null)
  const bottomSentinelRef = useRef(null)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false)
  const [needScrollToEnable, setNeedScrollToEnable] = useState(false)
  const [hasReachedBottomOnce, setHasReachedBottomOnce] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [declineModalOpen, setDeclineModalOpen] = useState(false)
  const policies = useSelector(selectAuthUser)
  const isArabicSiteLanguage = language === 'AR'

  useEffect(() => {
    dispatch(userAuthGetPolicies())
  }, [dispatch])

  useEffect(() => {
    const isAcceptedPolicies = CookiesStorageService.isAcceptedPolicies();
    if (isAcceptedPolicies) {
      history.push(AppRoutes.landing);
    }
  }, []);

  const consentText = useMemo(() => (isArabicSiteLanguage
    ? policies?.policies?.message_ar
    : policies?.policies?.message_en), [isArabicSiteLanguage, policies])

  useEffect(() => {
    const node = contentRef.current
    const sentinel = bottomSentinelRef.current
    if (!node || !sentinel) return undefined

    const hasScrollableOverflow = () => node.scrollHeight > node.clientHeight + 4
    const updateScrollProgress = () => {
      const maxScrollTop = node.scrollHeight - node.clientHeight
      if (maxScrollTop <= 0) {
        setScrollProgress(100)
        return
      }
      setScrollProgress(Math.min((node.scrollTop / maxScrollTop) * 100, 100))
    }
    const setScrollState = (bottomVisible) => {
      const overflowNow = hasScrollableOverflow()
      setNeedScrollToEnable(overflowNow)
      setIsScrolledToBottom(!overflowNow || bottomVisible)
    }

    setIsChecked(false)
    setHasReachedBottomOnce(false)
    node.scrollTop = 0
    setScrollState(false)
    updateScrollProgress()

    const observer = new IntersectionObserver(
      ([entry]) => {
        setScrollState(entry.isIntersecting)
        if (entry.isIntersecting) setScrollProgress(100)
      },
      {
        root: node,
        rootMargin: '0px 0px 8px 0px',
        threshold: 1,
      },
    )

    node.addEventListener('scroll', updateScrollProgress, { passive: true })
    observer.observe(sentinel)
    return () => {
      observer.disconnect()
      node.removeEventListener('scroll', updateScrollProgress)
    }
  }, [consentText, isArabicSiteLanguage])

  useEffect(() => {
    if (isScrolledToBottom) {
      setHasReachedBottomOnce(true)
    }
  }, [isScrolledToBottom])

  const shouldShowScrollWarning = needScrollToEnable && !hasReachedBottomOnce

  const onAccept = () => {
    dispatch(userAuthAcceptPolicies())
      .then((res) => {
        if (res?.meta?.requestStatus === 'fulfilled') {
          history.push(AppRoutes.landing)
        }
      })
  }

  const onDecline = () => {
    CookiesStorageService.clearCookies()
    history.push(AppRoutes.login)
  }

  const onOpenDeclineModal = () => {
    setDeclineModalOpen(true)
  }

  const onCloseDeclineModal = () => {
    setDeclineModalOpen(false)
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.card}>
        <Box sx={styles.header}>
          <Box sx={styles.headerIconWrap}>
            <ShieldCheckIcon style={styles.headerIcon} />
          </Box>
          <Box>
            <Typography variant="body2" sx={styles.headerTag}>
              <SvgIconSize icon={<LockIcon style={styles.lockIcon} />} size="sm" />{t?.['REQUIRED CONSENT']}</Typography>
            <Typography sx={styles.headerTitle}>{t?.['Consent Form']}</Typography>
          </Box>
        </Box>
        <Box
          sx={styles.progressTrack}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(scrollProgress)}
        >
          <Box sx={styles.progressFill(scrollProgress)} />
        </Box>

        <Box
          ref={contentRef}
          sx={styles.content}
        >
          <Typography
            component="div"
            dir={isArabicSiteLanguage ? 'rtl' : 'ltr'}
            sx={styles.contentText}
          >
            {decodeHtmlString(consentText)}
          </Typography>
          <Box ref={bottomSentinelRef} sx={styles.bottomSentinel} />
        </Box>

        {shouldShowScrollWarning && (
          <Box sx={styles.warning}>
            <Box sx={styles.warningIconWrap}>
              <PriorityHighRoundedIcon sx={styles.warningIcon} />
            </Box>
            <Typography sx={styles.warningText}>
              {t?.['Please scroll to the end of the consent form to enable consent.']}
            </Typography>
          </Box>
        )}

        <Box sx={styles.checkboxRow(isChecked)}>
          <FormControlLabel
            sx={styles.checkboxLabel}
            control={(
              <Checkbox
                checked={isChecked}
                disabled={!isScrolledToBottom}
                onChange={(e) => setIsChecked(e.target.checked)}
                sx={styles.consentCheckbox}
              />
            )}
            label={(
              <Typography
                sx={styles.checkboxText}
              >
                <Box component="span">
                  {t?.['I have read and understood this consent form and ']}
                </Box>
                <Box component="span" sx={styles.checkboxTextEmphasis}>
                  {t?.['freely give my consent ']}
                </Box>
                <Box component="span">
                  {t?.['to all what described above.']}
                </Box>
              </Typography>
            )}
          />
        </Box>

        <Box sx={styles.actions}>
          <Button variant="outlined" onClick={onOpenDeclineModal} sx={styles.declineBtn}>
            {t?.['Decline & sign out']}
          </Button>
          <Button
            variant="contained"
            onClick={onAccept}
            disabled={!isChecked}
            sx={styles.acceptBtn}
            startIcon={<CheckSignIcon />}
          >
            {t?.['I consent & continue']}
          </Button>
        </Box>
      </Box>
      <DeclineConsentFormModal
        open={declineModalOpen}
        onClose={onCloseDeclineModal}
        onConfirm={onDecline}
      />
       <Box sx={styles.backSvg}>
          <LoginBackSvg style={styles.backSvg} />
      </Box>
      <LoaderWithErrorHandler
        store={policies}
        resetAlertHandler={userAuthResetAlertHandler} />
    </Box>
  )
}

const styles = {
  root: {
    '& header': {
      marginBottom: '100px',
    },
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e8eaed',
    borderRadius: '16px',
    overflow: 'hidden',
    px: 0,
    py: 0,
    zIndex: 3,
    boxSizing: 'border-box',
    width: { xs: 'auto', sm: '700px' },
    maxWidth: { xs: 'calc(100% - 32px)', sm: '700px' },
    mx: { xs: 2, sm: 'auto' },
  },
  header: {
    p: 2.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },
  progressTrack: {
    height: 3,
    width: '100%',
    backgroundColor: '#e8eaed',
    overflow: 'hidden',
  },
  progressFill: (progress) => ({
    height: '100%',
    width: `${progress}%`,
    backgroundColor: '#29CC8B',
    transition: 'width 120ms ease-out',
  }),
  headerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: '12px',
    backgroundColor: '#29CC8B',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerIcon: {
    width: 24,
    height: 24,
    color: '#fff',
  },
  headerTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#19935f',
    fontSize: '0.6rem',
  },
  lockIcon: {
    width: 14,
    height: 14,
    color: '#19935f',
  },
  headerTitle: {
    color: '#1b2559',
    fontWeight: 700,
    fontSize: '1.9rem',
    lineHeight: 1.2,
  },
  content: {
    maxHeight: 330,
    overflowY: 'auto',
    p: 2.5,
    borderBottom: '1px solid #ebecef',
  },
  contentText: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    fontSize: '0.95rem',
    lineHeight: 1.7,
    color: '#1f2937',
  },
  warning: {
    m: 2,
    border: '1px solid #f4d26a',
    borderRadius: '10px',
    backgroundColor: '#fffaf0',
    px: 1.5,
    py: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  warningIconWrap: {
    width: 20,
    height: 20,
    borderRadius: '999px',
    backgroundColor: '#d97706',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  warningIcon: {
    color: '#fff',
    fontSize: 14,
  },
  warningText: {
    fontSize: '0.875rem',
    color: '#8a6d1a',
  },
  checkboxRow: (isChecked) => ({
    m: 2,
    border: isChecked ? '1px solid #3eca7f' : '1px solid #e8eaed',
    borderRadius: '10px',
    display: 'block',
    px: 1,
    py: 1,
    backgroundColor: isChecked ? '#f4fcf7' : '#fff',
    transition: 'all 120ms ease-in-out',
    '&:hover': {
      borderColor: '#3eca7f',
      backgroundColor: '#f4fcf7',
    },
  }),
  checkboxLabel: {
    m: 0,
    width: '100%',
    alignItems: 'center',
    '& .MuiCheckbox-root': {
      mt: 0.25,
    },
  },
  checkboxText: {
    mt: 0.9,
    color: '#6b7280',
    fontSize: '0.95rem',
  },
  checkboxTextEmphasis: {
    fontWeight: 700,
  },
  consentCheckbox: {
    mt: '2px',
    color: '#c6ccd4',
    '&.Mui-checked': {
      color: '#29CC8B',
    },
    '&.Mui-disabled': {
      color: '#d4dbe3',
    },
    '&.Mui-disabled.Mui-checked': {
      color: '#9fdcc1',
    },
  },
  actions: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1.6fr' },
    gap: 1.5,
    p: 2,
    pt: 0,
  },
  declineBtn: {
    minHeight: 44,
    textTransform: 'none',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: '#29CC8B',
      color: '#fff',
    },
  },
  acceptBtn: {
    minHeight: 44,
    textTransform: 'none',
    borderRadius: '10px',
    backgroundColor: '#29CC8B',
    color: '#fff',
    '&:disabled': {
      backgroundColor: '#a8dbc5',
      color: '#fff',
    },
    '& .MuiButton-startIcon': {
      marginInlineEnd: '6px',
    },
    '& .MuiButton-startIcon svg': {
      width: 16,
      height: 16,
      strokeWidth: 3,
    },
  },
  backSvg: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: -10,
    minHeight: '100vh',
    width: '100%',
  },
}

export default Consent
